import { FormEvent, useEffect, useRef, useState } from "react";
import { Lock, LogIn, PackageCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminPasswordInput from "../admin/components/AdminPasswordInput";
import { fetchErpCsrf, getCurrentErpUser, loginErp } from "./api";

export default function ErpLogin() {
  const navigate = useNavigate();
  const usernameInputRef = useRef<HTMLInputElement | null>(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchErpCsrf()
      .then(() => getCurrentErpUser())
      .then(() => navigate("/erp/dashboard", { replace: true }))
      .catch(() => undefined);
  }, [navigate]);

  useEffect(() => {
    usernameInputRef.current?.focus();
  }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage("");
    if (!username.trim() || !password) {
      setMessage("請輸入帳號與密碼。");
      return;
    }

    setSubmitting(true);
    try {
      await loginErp(username.trim(), password);
      navigate("/erp/dashboard", { replace: true });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "登入失敗，請確認帳號密碼。");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="min-h-screen bg-[#f5f7f4] text-[#24352d]">
      <div className="mx-auto flex min-h-screen w-full max-w-[1180px] items-center justify-center px-5 py-10">
        <section className="grid w-full max-w-[980px] overflow-hidden rounded-lg border border-[#dfe6e1] bg-white shadow-[0_22px_70px_rgba(32,61,50,.12)] md:grid-cols-[.95fr_1.05fr]">
          <div className="bg-[#203d32] p-8 text-white md:p-10">
            <div className="mb-10">
              <div className="text-xl font-extrabold tracking-[.08em]">
                富聆聽<span className="text-[#d4a574]">健康</span>
              </div>
              <div className="mt-1 text-xs font-bold tracking-[.22em] text-[#d8c8b1]">ERP PORTAL</div>
            </div>

            <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-md bg-white/10 text-[#d8c8b1]">
              <PackageCheck size={24} />
            </div>
            <h1 className="mb-4 text-3xl font-bold leading-tight md:text-4xl">ERP 系統</h1>
            <p className="text-base leading-8 text-[#d9e4de]">供採購、倉管、工程與財務人員處理庫存、採購、入庫、出庫與門市訂單交付流程。</p>
          </div>

          <form className="grid gap-5 p-8 md:p-10" onSubmit={handleSubmit}>
            <div>
              <p className="mb-2 text-sm font-bold tracking-[.16em] text-[#315746]">ERP LOGIN</p>
              <h2 className="text-2xl font-bold text-[#203d32]">ERP 人員登入</h2>
            </div>

            <div>
              <label className="mb-2 block text-sm font-bold text-[#315746]" htmlFor="erp-username">帳號</label>
              <input
                autoComplete="username"
                className="h-12 w-full rounded-md border border-[#cfd8d2] px-4 outline-none transition focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                id="erp-username"
                ref={usernameInputRef}
                maxLength={80}
                value={username}
                onChange={(event) => setUsername(event.target.value)}
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-bold text-[#315746]" htmlFor="erp-password">密碼</label>
              <AdminPasswordInput
                autoComplete="current-password"
                className="h-12 w-full rounded-md border border-[#cfd8d2] px-4 outline-none transition focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                id="erp-password"
                value={password}
                onChange={setPassword}
              />
            </div>

            {message ? <div className="rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{message}</div> : null}

            <button className="inline-flex h-12 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32] disabled:cursor-not-allowed disabled:opacity-60" disabled={submitting} type="submit">
              {submitting ? <Lock size={18} /> : <LogIn size={18} />}
              {submitting ? "登入中" : "登入 ERP"}
            </button>
          </form>
        </section>
      </div>
    </main>
  );
}
